import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type {
  CounselorMeritBadge,
  CounselorYouth,
  MeritBadge,
  MeritBadgeCounselorProfile,
} from '@appTypes/esb';

import { initialState } from './constants';
import type { BulkEntryState, MBVersion } from './types';

export const bulkEntrySlice = createSlice({
  name: 'mbcBulkEntry',
  initialState,
  reducers: {
    resetState() {
      return initialState;
    },
    setMBCProfile(
      state,
      action: PayloadAction<{ mbcProfile?: MeritBadgeCounselorProfile }>,
    ) {
      state.mbcProfile = action.payload.mbcProfile;
    },
    toggleYouth(
      state,
      action: PayloadAction<{
        youth: CounselorYouth;
        checked: boolean;
      }>,
    ) {
      const { youth, checked } = action.payload;
      if (checked) {
        state.selectedYouth.push(youth);
      } else {
        state.selectedYouth = state.selectedYouth.filter(
          item => item.memberId !== youth.memberId,
        );
      }
    },
    setSelection(
      state,
      action: PayloadAction<{
        counselorMB: CounselorMeritBadge;
        mb: MeritBadge;
      }>,
    ) {
      state.validationErrors = initialState.validationErrors;
      state.selectedYouth = initialState.selectedYouth;
      state.selectedRequirements = initialState.selectedRequirements;
      state.completionDate = initialState.completionDate;
      state.comment = initialState.comment;
      state.counselorApproved = initialState.counselorApproved;
      state.selectedVersion = undefined;
      state.mb = action.payload.mb;
      state.counselorMB = action.payload.counselorMB;
    },
    preselect(
      state,
      action: PayloadAction<{
        counselorMB: CounselorMeritBadge;
        mb: MeritBadge;
        selectedVersion?: MBVersion;
        selectedYouth: CounselorYouth[];
      }>,
    ) {
      const { counselorMB, mb, selectedVersion, selectedYouth } =
        action.payload;
      state.validationErrors = initialState.validationErrors;
      state.selectedRequirements = initialState.selectedRequirements;
      state.completionDate = initialState.completionDate;
      state.comment = initialState.comment;
      state.counselorApproved = initialState.counselorApproved;
      state.counselorMB = counselorMB;
      state.mb = mb;
      state.selectedVersion = selectedVersion;
      state.selectedYouth = selectedYouth;
    },
    setSelectedVersion(state, action: PayloadAction<MBVersion>) {
      state.selectedVersion = action.payload;
      state.selectedYouth = initialState.selectedYouth;
      state.selectedRequirements = initialState.selectedRequirements;
      state.completionDate = initialState.completionDate;
      state.comment = initialState.comment;
      state.counselorApproved = initialState.counselorApproved;
    },
    setValidationResults(
      state,
      action: PayloadAction<{
        validationErrors: BulkEntryState['validationErrors'];
      }>,
    ) {
      state.validationErrors = action.payload.validationErrors;
    },
    toggleRequirement(
      state,
      action: PayloadAction<{ requirementId: number; toggle: boolean }>,
    ) {
      const { requirementId, toggle } = action.payload;
      const reqs = new Set(state.selectedRequirements);
      if (toggle) {
        reqs.add(requirementId);
      } else {
        reqs.delete(requirementId);
      }
      state.selectedRequirements = [...reqs.values()];
    },
    toggleAllRequirements(
      state,
      action: PayloadAction<{ toggle: boolean; requirementIds: number[] }>,
    ) {
      if (action.payload.toggle) {
        state.selectedRequirements = action.payload.requirementIds;
      } else {
        state.selectedRequirements = [];
      }
    },
    setCompletionDate(state, action: PayloadAction<Date | undefined>) {
      state.completionDate = action.payload;
    },
    setComment(state, action: PayloadAction<string>) {
      state.comment = action.payload;
    },
    setCounselorApproved(state, action: PayloadAction<boolean>) {
      state.counselorApproved = action.payload;
    },
    resetAfterSubmit(state) {
      state.selectedRequirements = initialState.selectedRequirements;
      state.completionDate = initialState.completionDate;
      state.comment = initialState.comment;
      state.counselorApproved = initialState.counselorApproved;
      state.selectedYouth = initialState.selectedYouth;
    },
  },
});

export const bulkEntryActions = bulkEntrySlice.actions;
